import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Collapse,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowDropDown as ArrowDropDownIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  SkipNext as SkipNextIcon,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
  DragIndicator as DragIndicatorIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../contexts/AuthContext';
import vendedorService, { Vendedor } from '../../services/vendedorService';
import visitasPlanService from '../../services/visitasPlanService';
import visitasService from '../../services/visitasService';
import {
  CreateVisitasDto,
  CreateVisitasPlanDto,
  CreateVisitasRapidaDto,
  PagedResult,
  UpdateVisitasEstadoDto,
  UpdateVisitasPlanDto,
  VisitasDto,
  VisitasHistorialDto,
  VisitasPlanIndicadoresDto,
  VisitasPlanDto,
} from '../../types/visitas';
import PlanFormDialog from './components/PlanFormDialog';
import AppendVisitaDialog from './components/AppendVisitaDialog';
import VisitaRapidaDialog from './components/VisitaRapidaDialog';
import VisitaEstadoDialog from './components/VisitaEstadoDialog';
import VisitaHistorialDialog from './components/VisitaHistorialDialog';
import PlanIndicadoresPanel from './components/PlanIndicadoresPanel';
import VisitasPlanSearchDialog from './components/VisitasPlanSearchDialog';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import { exportVisitasPlanPdf } from './exportVisitasPlanPdf';

type TabValue = 'planes' | 'visitas';

interface VendedorOption {
  nroVendedor: number;
  nombreVendedor?: string;
}

interface PlanOption {
  idVisitaPlan: number;
  fecha: string;
  nroVendedor: number;
  nombreVendedor?: string | null;
}

type PlanActionTarget = {
  idVisitaPlan: number;
  fecha: string;
  nroVendedor: number;
  nombreVendedor?: string | null;
  usuarioPlanificador?: string | null;
  fechaCreacion: string;
  estadoPlan: string;
  observaciones?: string | null;
  origenCreacion?: string | null;
};

const emptyPaged = <T,>(): PagedResult<T> => ({
  items: [],
  totalCount: 0,
  pageNumber: 1,
  pageSize: 10,
  totalPages: 0,
});

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('es-AR');
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('es-AR');
};

const renderClientePlanDetalle = (v: VisitasDto) => {
  if (v.nombreCliente) {
    return (
      <Box>
        <Typography variant="body2" component="span">
          {v.nombreCliente}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          N° {v.nroCliente}
        </Typography>
      </Box>
    );
  }
  return <Typography variant="body2">{v.nroCliente}</Typography>;
};

const renderDireccionPlanDetalle = (v: VisitasDto) => {
  const cod = v.codigoDireccion?.trim();
  if (v.descripcionDireccion) {
    return (
      <Box>
        <Typography variant="body2" component="span">
          {v.descripcionDireccion}
        </Typography>
        {cod ? (
          <Typography variant="caption" color="text.secondary" display="block">
            Código: {cod}
          </Typography>
        ) : null}
      </Box>
    );
  }
  if (cod) {
    return <Typography variant="body2">{cod}</Typography>;
  }
  return <Typography variant="body2">-</Typography>;
};

const formatVendedor = (nroVendedor: number, nombreVendedor?: string | null): string =>
  nombreVendedor ? `${nroVendedor} - ${nombreVendedor}` : String(nroVendedor);

const toPlanDto = (plan: PlanActionTarget): VisitasPlanDto => ({
  idVisitaPlan: plan.idVisitaPlan,
  fecha: plan.fecha,
  nroVendedor: plan.nroVendedor,
  usuarioPlanificador: plan.usuarioPlanificador,
  fechaCreacion: plan.fechaCreacion,
  estadoPlan: plan.estadoPlan,
  observaciones: plan.observaciones,
  origenCreacion: plan.origenCreacion,
  visitas: null,
});

const normalizeEstado = (estado?: string | null): string => String(estado || '').trim().toLowerCase();
const isVisitaPendiente = (visita: VisitasDto): boolean =>
  normalizeEstado(visita.estado) === normalizeEstado('Pendiente');

const sortVisitasByOrden = (rows: VisitasDto[]): VisitasDto[] =>
  [...rows].sort((a, b) => (a.orden - b.orden) || (a.idVisita - b.idVisita));

const pendingOrderSignature = (rows: VisitasDto[]): string =>
  sortVisitasByOrden(rows)
    .filter(isVisitaPendiente)
    .map((v) => v.idVisita)
    .join(',');

const reorderOnlyPendientes = (rows: VisitasDto[], pendingIdsInNewOrder: number[]): VisitasDto[] => {
  const sorted = sortVisitasByOrden(rows);
  const pendientes = sorted.filter(isVisitaPendiente);
  const pendingSlots = pendientes.map((v) => v.orden).sort((a, b) => a - b);
  const pendingById = new Map(pendientes.map((v) => [v.idVisita, v]));

  const updatedPending = new Map<number, VisitasDto>();
  pendingIdsInNewOrder.forEach((idVisita, idx) => {
    const row = pendingById.get(idVisita);
    if (!row) return;
    updatedPending.set(idVisita, {
      ...row,
      orden: pendingSlots[idx],
    });
  });

  return sortVisitasByOrden(sorted.map((row) => updatedPending.get(row.idVisita) || row));
};

interface SortablePlanVisitaRowProps {
  visita: VisitasDto;
}

const SortablePlanVisitaRow: React.FC<SortablePlanVisitaRowProps> = ({ visita }) => {
  const draggable = isVisitaPendiente(visita);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(visita.idVisita),
    disabled: !draggable,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
    backgroundColor: isDragging ? '#f7fbff' : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          {draggable ? (
            <IconButton
              size="small"
              color="primary"
              title="Arrastrar para reordenar (solo pendientes)"
              {...attributes}
              {...listeners}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          ) : (
            <LockIcon fontSize="small" color="disabled" titleAccess="Orden bloqueado por estado" />
          )}
          <Typography variant="body2">{visita.idVisita}</Typography>
        </Stack>
      </TableCell>
      <TableCell>{visita.orden}</TableCell>
      <TableCell>{renderClientePlanDetalle(visita)}</TableCell>
      <TableCell>{renderDireccionPlanDetalle(visita)}</TableCell>
      <TableCell>{visita.estado}</TableCell>
      <TableCell>{formatDateTime(visita.fechaHoraProgramada)}</TableCell>
    </TableRow>
  );
};

const Visitas: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const initialTab: TabValue = tabParam === 'visitas' ? 'visitas' : 'planes';
  const [tab, setTab] = useState<TabValue>(initialTab);

  const normalizedRoles = useMemo(
    () => (user?.roles || []).map((r) => String(r).trim().toLowerCase()),
    [user?.roles],
  );
  const canManageVisitas = useMemo(
    () => normalizedRoles.some((r) => ['admin', 'superadmin', 'gerente'].includes(r)),
    [normalizedRoles],
  );

  const [vendedores, setVendedores] = useState<VendedorOption[]>([]);

  const [planesData, setPlanesData] = useState<PagedResult<VisitasPlanIndicadoresDto>>(emptyPaged());
  const [planesLoading, setPlanesLoading] = useState(false);
  const [planesPage, setPlanesPage] = useState(0);
  const [planesRowsPerPage, setPlanesRowsPerPage] = useState(10);
  const [expandedPlanRows, setExpandedPlanRows] = useState<Set<number>>(new Set());

  const [visitasData, setVisitasData] = useState<PagedResult<VisitasDto>>(emptyPaged());
  const [visitasLoading, setVisitasLoading] = useState(false);
  const [visitasPage, setVisitasPage] = useState(0);
  const [visitasRowsPerPage, setVisitasRowsPerPage] = useState(10);
  const [visitasPlanOptions, setVisitasPlanOptions] = useState<PlanOption[]>([]);
  const [visitasPlanOptionsLoading, setVisitasPlanOptionsLoading] = useState(false);

  const [planesFilters, setPlanesFilters] = useState({
    fechaDesde: '',
    fechaHasta: '',
    nroVendedor: '',
    estadoPlan: '',
  });
  const [visitasFilters, setVisitasFilters] = useState({
    idVisitaPlan: '',
    nroVendedor: '',
    fechaDesde: '',
    fechaHasta: '',
    estado: '',
    nroCliente: '',
  });

  const [planCreateOpen, setPlanCreateOpen] = useState(false);
  const [planEditOpen, setPlanEditOpen] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<VisitasPlanDto | null>(null);

  const [planDetailOpen, setPlanDetailOpen] = useState(false);
  const [planDetailLoading, setPlanDetailLoading] = useState(false);
  const [planDetail, setPlanDetail] = useState<VisitasPlanDto | null>(null);
  const [planDetailVisitasDraft, setPlanDetailVisitasDraft] = useState<VisitasDto[]>([]);
  const [planOrderDirty, setPlanOrderDirty] = useState(false);
  const [planOrderSaving, setPlanOrderSaving] = useState(false);

  const [appendOpen, setAppendOpen] = useState(false);
  const [appendSaving, setAppendSaving] = useState(false);
  const [appendPlan, setAppendPlan] = useState<PlanActionTarget | null>(null);

  const [planPdfLoadingId, setPlanPdfLoadingId] = useState<number | null>(null);

  const [visitaRapidaOpen, setVisitaRapidaOpen] = useState(false);
  const [visitaRapidaSaving, setVisitaRapidaSaving] = useState(false);

  const [estadoOpen, setEstadoOpen] = useState(false);
  const [estadoSaving, setEstadoSaving] = useState(false);
  const [estadoAction, setEstadoAction] = useState<'iniciar' | 'completar' | 'omitir'>('iniciar');
  const [selectedVisita, setSelectedVisita] = useState<VisitasDto | null>(null);

  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [historialItems, setHistorialItems] = useState<VisitasHistorialDto[]>([]);
  const [historialVisitaId, setHistorialVisitaId] = useState<number | null>(null);

  const [clienteFiltroSearchOpen, setClienteFiltroSearchOpen] = useState(false);
  const [visitaFiltroClienteLabel, setVisitaFiltroClienteLabel] = useState('');
  const [planFiltroSearchOpen, setPlanFiltroSearchOpen] = useState(false);
  const [visitaFiltroPlanLabel, setVisitaFiltroPlanLabel] = useState('');

  const planDetailSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    const nextTab: TabValue = tabParam === 'visitas' ? 'visitas' : 'planes';
    setTab(nextTab);
  }, [tabParam]);

  const loadVendedores = useCallback(async () => {
    try {
      const data = await vendedorService.getVendedoresPaged({
        pageNumber: 1,
        pageSize: 200,
      });
      setVendedores(
        (data.items || []).map((v: Vendedor) => ({
          nroVendedor: v.nroVendedor,
          nombreVendedor: v.nombreVendedor,
        })),
      );
    } catch {
      setVendedores([]);
    }
  }, []);

  const fetchPlanes = useCallback(async () => {
    try {
      setPlanesLoading(true);
      const data = await visitasPlanService.getPlanesIndicadores({
        fechaDesde: planesFilters.fechaDesde || undefined,
        fechaHasta: planesFilters.fechaHasta || undefined,
        nroVendedor: planesFilters.nroVendedor ? Number(planesFilters.nroVendedor) : undefined,
        estadoPlan: planesFilters.estadoPlan || undefined,
        pageNumber: planesPage + 1,
        pageSize: planesRowsPerPage,
      });
      setPlanesData(data);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al cargar planes de visitas', {
        variant: 'error',
      });
      setPlanesData(emptyPaged());
    } finally {
      setPlanesLoading(false);
    }
  }, [enqueueSnackbar, planesFilters, planesPage, planesRowsPerPage]);

  const fetchVisitas = useCallback(async () => {
    try {
      setVisitasLoading(true);
      const data = await visitasService.getVisitas({
        idVisitaPlan: visitasFilters.idVisitaPlan ? Number(visitasFilters.idVisitaPlan) : undefined,
        nroVendedor: visitasFilters.nroVendedor ? Number(visitasFilters.nroVendedor) : undefined,
        fechaDesde: visitasFilters.fechaDesde || undefined,
        fechaHasta: visitasFilters.fechaHasta || undefined,
        estado: visitasFilters.estado || undefined,
        nroCliente: visitasFilters.nroCliente ? Number(visitasFilters.nroCliente) : undefined,
        pageNumber: visitasPage + 1,
        pageSize: visitasRowsPerPage,
      });
      setVisitasData(data);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al cargar visitas', {
        variant: 'error',
      });
      setVisitasData(emptyPaged());
    } finally {
      setVisitasLoading(false);
    }
  }, [enqueueSnackbar, visitasFilters, visitasPage, visitasRowsPerPage]);

  const fetchVisitasPlanOptions = useCallback(async () => {
    try {
      setVisitasPlanOptionsLoading(true);
      const data = await visitasPlanService.getPlanesIndicadores({
        fechaDesde: visitasFilters.fechaDesde || undefined,
        fechaHasta: visitasFilters.fechaHasta || undefined,
        nroVendedor: visitasFilters.nroVendedor ? Number(visitasFilters.nroVendedor) : undefined,
        pageNumber: 1,
        pageSize: 200,
      });
      setVisitasPlanOptions(
        (data.items || []).map((p) => ({
          idVisitaPlan: p.idVisitaPlan,
          fecha: p.fecha,
          nroVendedor: p.nroVendedor,
          nombreVendedor: p.nombreVendedor,
        })),
      );
    } catch {
      setVisitasPlanOptions([]);
    } finally {
      setVisitasPlanOptionsLoading(false);
    }
  }, [visitasFilters.fechaDesde, visitasFilters.fechaHasta, visitasFilters.nroVendedor]);

  useEffect(() => {
    loadVendedores();
  }, [loadVendedores]);

  useEffect(() => {
    if (tab === 'planes') fetchPlanes();
  }, [tab, fetchPlanes]);

  useEffect(() => {
    setExpandedPlanRows(new Set());
  }, [planesPage, planesRowsPerPage, planesFilters, tab]);

  useEffect(() => {
    if (tab === 'visitas') fetchVisitas();
  }, [tab, fetchVisitas]);

  useEffect(() => {
    if (tab === 'visitas') fetchVisitasPlanOptions();
  }, [tab, fetchVisitasPlanOptions]);

  const handleChangeTab = (_: React.SyntheticEvent, value: TabValue) => {
    setSearchParams({ tab: value });
  };

  const refreshAll = async () => {
    await Promise.all([fetchPlanes(), fetchVisitas()]);
  };

  const togglePlanIndicators = (idVisitaPlan: number) => {
    setExpandedPlanRows((prev) => {
      const next = new Set(prev);
      if (next.has(idVisitaPlan)) {
        next.delete(idVisitaPlan);
      } else {
        next.add(idVisitaPlan);
      }
      return next;
    });
  };

  const handleCreatePlan = async (payload: CreateVisitasPlanDto) => {
    try {
      setPlanSaving(true);
      await visitasPlanService.createPlan(payload);
      enqueueSnackbar('Plan creado correctamente', { variant: 'success' });
      setPlanCreateOpen(false);
      await fetchPlanes();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al crear el plan', {
        variant: 'error',
      });
    } finally {
      setPlanSaving(false);
    }
  };

  const handleUpdatePlan = async (payload: UpdateVisitasPlanDto) => {
    if (!selectedPlan) return;
    try {
      setPlanSaving(true);
      await visitasPlanService.updatePlan(selectedPlan.idVisitaPlan, payload);
      enqueueSnackbar('Plan actualizado correctamente', { variant: 'success' });
      setPlanEditOpen(false);
      setSelectedPlan(null);
      await fetchPlanes();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al actualizar el plan', {
        variant: 'error',
      });
    } finally {
      setPlanSaving(false);
    }
  };

  const handleDeletePlan = async (plan: PlanActionTarget) => {
    if (!window.confirm(`Se eliminará el plan #${plan.idVisitaPlan}. ¿Desea continuar?`)) return;
    try {
      await visitasPlanService.deletePlan(plan.idVisitaPlan);
      enqueueSnackbar('Plan eliminado correctamente', { variant: 'success' });
      await fetchPlanes();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar plan', {
        variant: 'error',
      });
    }
  };

  const handleImprimirPlanPdf = async (plan: PlanActionTarget) => {
    if (planPdfLoadingId === plan.idVisitaPlan) return;
    try {
      setPlanPdfLoadingId(plan.idVisitaPlan);
      const full = await visitasPlanService.getPlan(plan.idVisitaPlan);
      await exportVisitasPlanPdf(full, {
        vendedorLabel: formatVendedor(plan.nroVendedor, plan.nombreVendedor),
      });
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al generar PDF del plan', {
        variant: 'error',
      });
    } finally {
      setPlanPdfLoadingId(null);
    }
  };

  const handleOpenPlanDetail = async (plan: PlanActionTarget) => {
    try {
      setPlanDetailOpen(true);
      setPlanDetailLoading(true);
      const full = await visitasPlanService.getPlan(plan.idVisitaPlan);
      setPlanDetail(full);
      setPlanDetailVisitasDraft(sortVisitasByOrden(full.visitas || []));
      setPlanOrderDirty(false);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al obtener detalle del plan', {
        variant: 'error',
      });
      setPlanDetailOpen(false);
    } finally {
      setPlanDetailLoading(false);
    }
  };

  const handlePlanDetailDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = Number(active.id);
    const overId = Number(over.id);
    if (!Number.isFinite(activeId) || !Number.isFinite(overId)) return;

    const pendingIds = sortVisitasByOrden(planDetailVisitasDraft)
      .filter(isVisitaPendiente)
      .map((v) => v.idVisita);

    const oldIndex = pendingIds.indexOf(activeId);
    const newIndex = pendingIds.indexOf(overId);
    if (oldIndex < 0 || newIndex < 0) return;

    const reorderedPendingIds = arrayMove(pendingIds, oldIndex, newIndex);
    const nextRows = reorderOnlyPendientes(planDetailVisitasDraft, reorderedPendingIds);
    setPlanDetailVisitasDraft(nextRows);
    setPlanOrderDirty(pendingOrderSignature(nextRows) !== pendingOrderSignature(planDetail?.visitas || []));
  };

  const handleSavePlanOrder = async () => {
    if (!planDetail || !planOrderDirty) return;

    const payload = {
      items: sortVisitasByOrden(planDetailVisitasDraft)
        .filter(isVisitaPendiente)
        .map((v) => ({ idVisita: v.idVisita })),
    };

    try {
      setPlanOrderSaving(true);
      await visitasPlanService.reordenarPendientes(planDetail.idVisitaPlan, payload);
      enqueueSnackbar('Orden de visitas actualizado correctamente', { variant: 'success' });
      const full = await visitasPlanService.getPlan(planDetail.idVisitaPlan);
      setPlanDetail(full);
      setPlanDetailVisitasDraft(sortVisitasByOrden(full.visitas || []));
      setPlanOrderDirty(false);
      await Promise.all([fetchPlanes(), fetchVisitas()]);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar el nuevo orden de visitas', {
        variant: 'error',
      });
      if (planDetail?.idVisitaPlan) {
        const full = await visitasPlanService.getPlan(planDetail.idVisitaPlan);
        setPlanDetail(full);
        setPlanDetailVisitasDraft(sortVisitasByOrden(full.visitas || []));
        setPlanOrderDirty(false);
      }
    } finally {
      setPlanOrderSaving(false);
    }
  };

  const handleAppendVisita = async (payload: CreateVisitasDto) => {
    if (!appendPlan) return;
    try {
      setAppendSaving(true);
      await visitasPlanService.addVisitaToPlan(appendPlan.idVisitaPlan, payload);
      enqueueSnackbar('Visita agregada al plan correctamente', { variant: 'success' });
      setAppendOpen(false);
      setAppendPlan(null);
      await refreshAll();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al agregar visita al plan', {
        variant: 'error',
      });
    } finally {
      setAppendSaving(false);
    }
  };

  const handleCreateVisitaRapida = async (payload: CreateVisitasRapidaDto) => {
    try {
      setVisitaRapidaSaving(true);
      await visitasService.createVisitaRapida(payload);
      enqueueSnackbar('Visita rápida creada correctamente', { variant: 'success' });
      setVisitaRapidaOpen(false);
      await refreshAll();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al crear visita rápida', {
        variant: 'error',
      });
    } finally {
      setVisitaRapidaSaving(false);
    }
  };

  const openEstadoDialog = (action: 'iniciar' | 'completar' | 'omitir', visita: VisitasDto) => {
    setEstadoAction(action);
    setSelectedVisita(visita);
    setEstadoOpen(true);
  };

  const handleSubmitEstado = async (payload: UpdateVisitasEstadoDto) => {
    if (!selectedVisita) return;
    try {
      setEstadoSaving(true);
      if (estadoAction === 'iniciar') {
        await visitasService.iniciar(selectedVisita.idVisita, payload);
      } else if (estadoAction === 'completar') {
        await visitasService.completar(selectedVisita.idVisita, payload);
      } else {
        await visitasService.omitir(selectedVisita.idVisita, payload);
      }
      enqueueSnackbar('Estado actualizado correctamente', { variant: 'success' });
      setEstadoOpen(false);
      setSelectedVisita(null);
      await refreshAll();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al actualizar estado de visita', {
        variant: 'error',
      });
    } finally {
      setEstadoSaving(false);
    }
  };

  const handleOpenHistorial = async (visita: VisitasDto) => {
    try {
      setHistorialVisitaId(visita.idVisita);
      setHistorialItems([]);
      setHistorialOpen(true);
      setHistorialLoading(true);
      const rows = await visitasService.getHistorial(visita.idVisita);
      setHistorialItems(rows);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al cargar historial', {
        variant: 'error',
      });
      setHistorialOpen(false);
    } finally {
      setHistorialLoading(false);
    }
  };

  if (!canManageVisitas) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Alert severity="warning">
          No tiene permisos para administrar el módulo de visitas con el rol actual.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Visitas
            </Typography>
            <Stack direction="row" spacing={1}>
              {tab === 'planes' ? (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPlanCreateOpen(true)}>
                  Nuevo Plan
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setVisitaRapidaOpen(true)}
                >
                  Visita Rápida
                </Button>
              )}
            </Stack>
          </Stack>

          <Tabs value={tab} onChange={handleChangeTab} sx={{ mb: 2 }}>
            <Tab label="Planes" value="planes" />
            <Tab label="Visitas" value="visitas" />
          </Tabs>

          {tab === 'planes' && (
            <Box>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Fecha Desde"
                    type="date"
                    value={planesFilters.fechaDesde}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setPlanesFilters((p) => ({ ...p, fechaDesde: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Fecha Hasta"
                    type="date"
                    value={planesFilters.fechaHasta}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setPlanesFilters((p) => ({ ...p, fechaHasta: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="Vendedor"
                    value={planesFilters.nroVendedor}
                    onChange={(e) => setPlanesFilters((p) => ({ ...p, nroVendedor: e.target.value }))}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {vendedores.map((v) => (
                      <MenuItem key={v.nroVendedor} value={String(v.nroVendedor)}>
                        {v.nroVendedor} - {v.nombreVendedor || 'Sin nombre'}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    select
                    label="Estado"
                    value={planesFilters.estadoPlan}
                    onChange={(e) => setPlanesFilters((p) => ({ ...p, estadoPlan: e.target.value }))}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Abierto">Abierto</MenuItem>
                    <MenuItem value="EnCurso">EnCurso</MenuItem>
                    <MenuItem value="Cerrado">Cerrado</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={12} md={3}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setPlanesPage(0);
                        fetchPlanes();
                      }}
                    >
                      Buscar
                    </Button>
                    <Button
                      onClick={() => {
                        setPlanesFilters({
                          fechaDesde: '',
                          fechaHasta: '',
                          nroVendedor: '',
                          estadoPlan: '',
                        });
                        setPlanesPage(0);
                      }}
                    >
                      Limpiar
                    </Button>
                  </Stack>
                </Grid>
              </Grid>

              {isMobile ? (
                <Stack spacing={1.5}>
                  {planesData.items.map((plan) => (
                    <Card key={plan.idVisitaPlan} variant="outlined">
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Plan #{plan.idVisitaPlan}
                        </Typography>
                        <Typography variant="body2">Fecha: {formatDate(plan.fecha)}</Typography>
                        <Typography variant="body2">
                          Vendedor: {formatVendedor(plan.nroVendedor, plan.nombreVendedor)}
                        </Typography>
                        <Typography variant="body2">
                          Estado:{' '}
                          <Chip label={plan.estadoPlan} size="small" color="primary" variant="outlined" />
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <PlanIndicadoresPanel
                            visitas={plan.visitas}
                            cobranzas={plan.cobranzas}
                            ventas={plan.ventas}
                            entregas={plan.entregas}
                          />
                        </Box>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap">
                          <IconButton size="small" color="info" onClick={() => handleOpenPlanDetail(plan)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <Tooltip title="Imprimir / PDF">
                            <span>
                              <IconButton
                                size="small"
                                color="secondary"
                                disabled={planPdfLoadingId === plan.idVisitaPlan}
                                onClick={() => handleImprimirPlanPdf(plan)}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => {
                              setAppendPlan(plan);
                              setAppendOpen(true);
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setSelectedPlan(toPlanDto(plan));
                              setPlanEditOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeletePlan(plan)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 44 }} />
                        <TableCell>ID</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Vendedor</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Origen</TableCell>
                        <TableCell>Usuario</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {planesLoading ? (
                        <TableRow>
                          <TableCell colSpan={8}>Cargando...</TableCell>
                        </TableRow>
                      ) : planesData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8}>No hay datos para mostrar.</TableCell>
                        </TableRow>
                      ) : (
                        planesData.items.map((plan) => (
                          <React.Fragment key={plan.idVisitaPlan}>
                            <TableRow hover>
                              <TableCell>
                                <Tooltip
                                  title={
                                    expandedPlanRows.has(plan.idVisitaPlan)
                                      ? 'Ocultar indicadores'
                                      : 'Ver indicadores'
                                  }
                                >
                                  <IconButton size="small" onClick={() => togglePlanIndicators(plan.idVisitaPlan)}>
                                    {expandedPlanRows.has(plan.idVisitaPlan) ? (
                                      <KeyboardArrowUpIcon fontSize="small" />
                                    ) : (
                                      <KeyboardArrowDownIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                              <TableCell>{plan.idVisitaPlan}</TableCell>
                              <TableCell>{formatDate(plan.fecha)}</TableCell>
                              <TableCell>{formatVendedor(plan.nroVendedor, plan.nombreVendedor)}</TableCell>
                              <TableCell>
                                <Chip label={plan.estadoPlan} size="small" color="primary" variant="outlined" />
                              </TableCell>
                              <TableCell>{plan.origenCreacion || '-'}</TableCell>
                              <TableCell>{plan.usuarioPlanificador || '-'}</TableCell>
                              <TableCell align="center">
                                <Tooltip title="Ver detalle">
                                  <IconButton size="small" color="info" onClick={() => handleOpenPlanDetail(plan)}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Imprimir / PDF">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="secondary"
                                      disabled={planPdfLoadingId === plan.idVisitaPlan}
                                      onClick={() => handleImprimirPlanPdf(plan)}
                                    >
                                      <PrintIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Agregar visita">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => {
                                      setAppendPlan(plan);
                                      setAppendOpen(true);
                                    }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Editar plan">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      setSelectedPlan(toPlanDto(plan));
                                      setPlanEditOpen(true);
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar plan">
                                  <IconButton size="small" color="error" onClick={() => handleDeletePlan(plan)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={8} sx={{ py: 0, borderBottom: 0 }}>
                                <Collapse in={expandedPlanRows.has(plan.idVisitaPlan)} timeout="auto" unmountOnExit>
                                  <Box sx={{ px: 1, py: 1.25, bgcolor: 'grey.50', borderRadius: 1.5, mb: 1 }}>
                                    <PlanIndicadoresPanel
                                      visitas={plan.visitas}
                                      cobranzas={plan.cobranzas}
                                      ventas={plan.ventas}
                                      entregas={plan.entregas}
                                    />
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <TablePagination
                component="div"
                count={planesData.totalCount}
                page={planesPage}
                onPageChange={(_, newPage) => setPlanesPage(newPage)}
                rowsPerPage={planesRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setPlanesRowsPerPage(Number(e.target.value));
                  setPlanesPage(0);
                }}
                labelRowsPerPage="Filas por página:"
              />
            </Box>
          )}

          {tab === 'visitas' && (
            <Box>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label="IdPlan"
                    value={visitasFilters.idVisitaPlan}
                    onChange={(e) => {
                      setVisitasFilters((p) => ({ ...p, idVisitaPlan: e.target.value }));
                      setVisitaFiltroPlanLabel('');
                    }}
                    helperText={
                      visitaFiltroPlanLabel
                        ? visitaFiltroPlanLabel
                        : visitasPlanOptionsLoading
                          ? 'Cargando planes...'
                          : 'Escriba N° o use el selector para buscar'
                    }
                    FormHelperTextProps={{
                      sx: {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            color="primary"
                            title="Seleccionar plan"
                            onClick={() => setPlanFiltroSearchOpen(true)}
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    select
                    label="Vendedor"
                    value={visitasFilters.nroVendedor}
                    onChange={(e) => setVisitasFilters((p) => ({ ...p, nroVendedor: e.target.value }))}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {vendedores.map((v) => (
                      <MenuItem key={v.nroVendedor} value={String(v.nroVendedor)}>
                        {v.nroVendedor} - {v.nombreVendedor || 'Sin nombre'}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Fecha Desde"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={visitasFilters.fechaDesde}
                    onChange={(e) => setVisitasFilters((p) => ({ ...p, fechaDesde: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Fecha Hasta"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={visitasFilters.fechaHasta}
                    onChange={(e) => setVisitasFilters((p) => ({ ...p, fechaHasta: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    select
                    label="Estado"
                    value={visitasFilters.estado}
                    onChange={(e) => setVisitasFilters((p) => ({ ...p, estado: e.target.value }))}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Pendiente">Pendiente</MenuItem>
                    <MenuItem value="Iniciada">Iniciada</MenuItem>
                    <MenuItem value="Completada">Completada</MenuItem>
                    <MenuItem value="Omitida">Omitida</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Número de Cliente"
                    value={visitasFilters.nroCliente}
                    onChange={(e) => {
                      setVisitasFilters((p) => ({ ...p, nroCliente: e.target.value }));
                      setVisitaFiltroClienteLabel('');
                    }}
                    helperText={
                      visitaFiltroClienteLabel
                        ? visitaFiltroClienteLabel
                        : 'Escriba el número o use el selector para buscar'
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            color="primary"
                            title="Seleccionar cliente"
                            onClick={() => setClienteFiltroSearchOpen(true)}
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setVisitasPage(0);
                        fetchVisitas();
                      }}
                    >
                      Buscar
                    </Button>
                    <Button
                      onClick={() => {
                        setVisitasFilters({
                          idVisitaPlan: '',
                          nroVendedor: '',
                          fechaDesde: '',
                          fechaHasta: '',
                          estado: '',
                          nroCliente: '',
                        });
                        setVisitaFiltroPlanLabel('');
                        setVisitaFiltroClienteLabel('');
                        setVisitasPage(0);
                      }}
                    >
                      Limpiar
                    </Button>
                  </Stack>
                </Grid>
              </Grid>

              {isMobile ? (
                <Stack spacing={1.5}>
                  {visitasData.items.map((v) => (
                    <Card key={v.idVisita} variant="outlined">
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Visita #{v.idVisita}
                        </Typography>
                        <Typography variant="body2">Plan: {v.idVisitaPlan}</Typography>
                        <Typography variant="body2">Cliente: {v.nroCliente}</Typography>
                        <Typography variant="body2">Orden: {v.orden}</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Estado: <Chip label={v.estado} size="small" color="primary" variant="outlined" />
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {v.estado === 'Pendiente' && (
                            <IconButton size="small" color="primary" onClick={() => openEstadoDialog('iniciar', v)}>
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          )}
                          {v.estado === 'Iniciada' && (
                            <IconButton size="small" color="success" onClick={() => openEstadoDialog('completar', v)}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          )}
                          {(v.estado === 'Pendiente' || v.estado === 'Iniciada') && (
                            <IconButton size="small" color="warning" onClick={() => openEstadoDialog('omitir', v)}>
                              <SkipNextIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton size="small" color="info" onClick={() => handleOpenHistorial(v)}>
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Orden</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Programada</TableCell>
                        <TableCell>Inicio</TableCell>
                        <TableCell>Fin</TableCell>
                        <TableCell>Omitida</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visitasLoading ? (
                        <TableRow>
                          <TableCell colSpan={10}>Cargando...</TableCell>
                        </TableRow>
                      ) : visitasData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10}>No hay datos para mostrar.</TableCell>
                        </TableRow>
                      ) : (
                        visitasData.items.map((v) => (
                          <TableRow key={v.idVisita} hover>
                            <TableCell>{v.idVisita}</TableCell>
                            <TableCell>{v.idVisitaPlan}</TableCell>
                            <TableCell>{v.orden}</TableCell>
                            <TableCell>{v.nroCliente}</TableCell>
                            <TableCell>
                              <Chip label={v.estado} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell>{formatDateTime(v.fechaHoraProgramada)}</TableCell>
                            <TableCell>{formatDateTime(v.fechaHoraInicio)}</TableCell>
                            <TableCell>{formatDateTime(v.fechaHoraFin)}</TableCell>
                            <TableCell>{formatDateTime(v.fechaHoraOmitida)}</TableCell>
                            <TableCell align="center">
                              {v.estado === 'Pendiente' && (
                                <Tooltip title="Iniciar visita">
                                  <IconButton size="small" color="primary" onClick={() => openEstadoDialog('iniciar', v)}>
                                    <PlayArrowIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {v.estado === 'Iniciada' && (
                                <Tooltip title="Completar visita">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => openEstadoDialog('completar', v)}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {(v.estado === 'Pendiente' || v.estado === 'Iniciada') && (
                                <Tooltip title="Omitir visita">
                                  <IconButton size="small" color="warning" onClick={() => openEstadoDialog('omitir', v)}>
                                    <SkipNextIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Ver historial">
                                <IconButton size="small" color="info" onClick={() => handleOpenHistorial(v)}>
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <TablePagination
                component="div"
                count={visitasData.totalCount}
                page={visitasPage}
                onPageChange={(_, newPage) => setVisitasPage(newPage)}
                rowsPerPage={visitasRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setVisitasRowsPerPage(Number(e.target.value));
                  setVisitasPage(0);
                }}
                labelRowsPerPage="Filas por página:"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <PlanFormDialog
        open={planCreateOpen}
        mode="create"
        vendedores={vendedores}
        saving={planSaving}
        onClose={() => setPlanCreateOpen(false)}
        onSubmitCreate={handleCreatePlan}
        onSubmitEdit={handleUpdatePlan}
      />

      <PlanFormDialog
        open={planEditOpen}
        mode="edit"
        vendedores={vendedores}
        plan={selectedPlan}
        saving={planSaving}
        onClose={() => {
          setPlanEditOpen(false);
          setSelectedPlan(null);
        }}
        onSubmitCreate={handleCreatePlan}
        onSubmitEdit={handleUpdatePlan}
      />

      <AppendVisitaDialog
        open={appendOpen}
        saving={appendSaving}
        onClose={() => {
          setAppendOpen(false);
          setAppendPlan(null);
        }}
        onSubmit={handleAppendVisita}
      />

      <VisitaRapidaDialog
        open={visitaRapidaOpen}
        vendedores={vendedores}
        saving={visitaRapidaSaving}
        onClose={() => setVisitaRapidaOpen(false)}
        onSubmit={handleCreateVisitaRapida}
      />

      <VisitaEstadoDialog
        open={estadoOpen}
        action={estadoAction}
        visita={selectedVisita}
        saving={estadoSaving}
        onClose={() => {
          setEstadoOpen(false);
          setSelectedVisita(null);
        }}
        onSubmit={handleSubmitEstado}
      />

      <VisitaHistorialDialog
        open={historialOpen}
        loading={historialLoading}
        visitaId={historialVisitaId}
        items={historialItems}
        onClose={() => {
          setHistorialOpen(false);
          setHistorialItems([]);
          setHistorialVisitaId(null);
        }}
      />

      <Dialog
        open={planDetailOpen}
        onClose={() => {
          setPlanDetailOpen(false);
          setPlanDetail(null);
          setPlanDetailVisitasDraft([]);
          setPlanOrderDirty(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalle del plan #{planDetail?.idVisitaPlan}</DialogTitle>
        <DialogContent dividers>
          {planDetailLoading ? (
            <Typography>Cargando...</Typography>
          ) : !planDetail ? (
            <Typography>No se pudo cargar el detalle.</Typography>
          ) : (
            <Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Fecha:</strong> {formatDate(planDetail.fecha)}
                </Typography>
                <Typography variant="body2">
                  <strong>Vendedor:</strong> {planDetail.nroVendedor}
                </Typography>
                <Typography variant="body2">
                  <strong>Estado:</strong> {planDetail.estadoPlan}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Observaciones:</strong> {planDetail.observaciones || '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Solo las visitas pendientes se pueden arrastrar. Las demás mantienen su orden fijo.
              </Typography>
              <DndContext sensors={planDetailSensors} collisionDetection={closestCenter} onDragEnd={handlePlanDetailDragEnd}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Orden</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Dirección</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Programada</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {planDetailVisitasDraft.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>No hay visitas asociadas.</TableCell>
                      </TableRow>
                    ) : (
                      <SortableContext
                        items={planDetailVisitasDraft.map((v) => String(v.idVisita))}
                        strategy={verticalListSortingStrategy}
                      >
                        {planDetailVisitasDraft.map((v) => (
                          <SortablePlanVisitaRow key={v.idVisita} visita={v} />
                        ))}
                      </SortableContext>
                    )}
                  </TableBody>
                </Table>
              </DndContext>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleSavePlanOrder} disabled={!planOrderDirty || planOrderSaving}>
            Guardar orden
          </Button>
          <Button
            onClick={() => {
              setPlanDetailOpen(false);
              setPlanDetail(null);
              setPlanDetailVisitasDraft([]);
              setPlanOrderDirty(false);
            }}
            disabled={planOrderSaving}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <ClienteSearch
        open={clienteFiltroSearchOpen}
        onClose={() => setClienteFiltroSearchOpen(false)}
        onSelect={(cliente) => {
          setVisitasFilters((p) => ({ ...p, nroCliente: String(cliente.nroCliente) }));
          setVisitaFiltroClienteLabel(
            [cliente.nombre, cliente.razonSocial].filter(Boolean).join(' — ') || `Cliente ${cliente.nroCliente}`,
          );
          setClienteFiltroSearchOpen(false);
        }}
      />

      <VisitasPlanSearchDialog
        open={planFiltroSearchOpen}
        loading={visitasPlanOptionsLoading}
        plans={visitasPlanOptions}
        onClose={() => setPlanFiltroSearchOpen(false)}
        onSelect={(plan) => {
          setVisitasFilters((p) => ({ ...p, idVisitaPlan: String(plan.idVisitaPlan) }));
          setVisitaFiltroPlanLabel(
            `#${plan.idVisitaPlan} - ${formatDate(plan.fecha)} - ${formatVendedor(plan.nroVendedor, plan.nombreVendedor)}`,
          );
          setPlanFiltroSearchOpen(false);
        }}
      />
    </Box>
  );
};

export default Visitas;
